import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";

interface Props {
  isVisible: boolean;
  title: string;
  anchorRef: React.RefObject<HTMLElement>;
  firstDatePlaceholder: string;
  secondDatePlaceholder?: string;
  disabled?: boolean;
  onApply: (firstDate: string, secondDate?: string) => void;
  onClose: () => void;
  firstDateInitValue?: string;
  secondDateInitValue?: string;
}

const FilterDatePicker: React.FC<Props> = ({
  isVisible,
  title,
  anchorRef,
  firstDatePlaceholder,
  secondDatePlaceholder,
  onApply,
  firstDateInitValue,
  secondDateInitValue,
  onClose,
}) => {
  const { t } = useTranslation();

  const [firstDate, setFirstDate] = useState<string>("");
  const [secondDate, setSecondDate] = useState<string>("");
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const OFFSET_Y = 90;

  useEffect(() => {
    if (!isVisible || !anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();

    setPosition({
      top: rect.top + window.scrollY - OFFSET_Y,
      left: rect.right + 10,
    });
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      setFirstDate(firstDateInitValue ?? "");
      setSecondDate(secondDateInitValue ?? "");
    }
  }, [firstDateInitValue, secondDateInitValue, isVisible]);

  const handleApply = () => {
    onApply(firstDate, secondDate ? secondDate : undefined);
    onClose();
  };

  const handleReset = () => {
    setFirstDate("");
    setSecondDate("");
    onApply("", "");
    onClose();
  };

  return createPortal(
    <div
      className={`filter-multiselect-wrapper filter-datepicker-wrapper ${
        isVisible ? "ready" : ""
      }`}
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="filter-multiselect-header">
        <div className="title">{title}</div>
        <div className="buttons">
          <div className="apply" onClick={handleApply}>
            {t("apply")}
          </div>
          <div className="reset" onClick={handleReset}>
            {t("reset")}
          </div>
        </div>
      </div>

      <div className="filter-datepicker-content">
        <div className="date-picker-wrapper">
          <div className="title">{firstDatePlaceholder}</div>
          <DatePicker
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={30}
            dateFormat="dd.MM.YYYY HH:mm"
            selected={
              firstDate ? moment(firstDate, "DD.MM.YYYY HH:mm").toDate() : null
            }
            onChange={(date) =>
              setFirstDate(date ? moment(date).format("DD.MM.YYYY HH:mm") : "")
            }
          />
        </div>

        {secondDatePlaceholder && (
          <div className="date-picker-wrapper">
            <div className="title">{secondDatePlaceholder}</div>
            <DatePicker
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={30}
              dateFormat="dd.MM.YYYY HH:mm"
              selected={
                secondDate
                  ? moment(secondDate, "DD.MM.YYYY HH:mm").toDate()
                  : null
              }
              onChange={(date) =>
                setSecondDate(
                  date ? moment(date).format("DD.MM.YYYY HH:mm") : ""
                )
              }
            />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default FilterDatePicker;
